import { Building2 } from "lucide-react";
import { logoUrl, getInitials } from "@/frontend/utils/shared";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/frontend/components/ui/tooltip";
import type { PatientRowHealthSystem } from "@/server/types/patient-status";

/**
 * Overlapping logo badges for the health systems on one patient row. Falls back
 * to the source's initials, then a generic icon, so a source without a curated
 * logo still reads as a distinct tile rather than an empty square.
 */
export function SourceLogoStack({
  healthSystems,
  totalCount,
}: {
  healthSystems: PatientRowHealthSystem[];
  totalCount: number;
}) {
  if (healthSystems.length === 0) {
    return <span className="text-xs text-muted-foreground">None yet</span>;
  }

  const overflow = totalCount - healthSystems.length;

  return (
    <div className="flex items-center gap-2">
      <div className="flex items-center">
        {healthSystems.map((system, index) => {
          const url = logoUrl(system.logo);
          const initials = system.name.trim() ? getInitials(system.name) : null;
          return (
            <Tooltip key={`${system.name}-${index}`}>
              <TooltipTrigger asChild>
                <span
                  className="flex size-6 items-center justify-center overflow-hidden rounded-md border bg-background not-first:-ml-1.5"
                  style={{ zIndex: healthSystems.length - index }}
                >
                  {url ? (
                    <img
                      src={url}
                      alt=""
                      className="size-4 object-contain"
                      data-ph-no-capture
                      onError={(event) => {
                        event.currentTarget.style.display = "none";
                        event.currentTarget.nextElementSibling?.classList.remove("hidden");
                      }}
                    />
                  ) : null}
                  <span className={url ? "hidden text-[10px] font-medium text-muted-foreground" : "text-[10px] font-medium text-muted-foreground"}>
                    {initials ?? <Building2 className="size-3" />}
                  </span>
                </span>
              </TooltipTrigger>
              <TooltipContent>{system.name}</TooltipContent>
            </Tooltip>
          );
        })}
      </div>
      <span className="text-xs tabular-nums text-muted-foreground">
        {overflow > 0 ? `+${overflow}` : totalCount}
      </span>
    </div>
  );
}
